import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";

const EmergencyServiceModal = ({ isOpen, onClose, onSave, serviceToEdit }) => {
  const isEditMode = !!serviceToEdit;
  const [serviceData, setServiceData] = useState({
    serviceName: "",
    particulars: "",
    providedBy: "",
    unitPrice: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && serviceToEdit) {
        setServiceData({
          serviceName: serviceToEdit.serviceName || "",
          particulars: serviceToEdit.particulars || "",
          providedBy: serviceToEdit.providedBy || "",
          unitPrice: serviceToEdit.unitPrice?.toString() || "",
        });
      } else {
        setServiceData({
          serviceName: "",
          particulars: "",
          providedBy: "",
          unitPrice: "",
        });
      }
      setErrors({});
    }
  }, [isOpen, serviceToEdit, isEditMode]);

  const validateForm = () => {
    const newErrors = {};
    if (!serviceData.serviceName.trim()) newErrors.serviceName = "Service Name is required";
    if (!serviceData.unitPrice.toString().trim() || isNaN(serviceData.unitPrice) || Number(serviceData.unitPrice) < 0) {
      newErrors.unitPrice = "Unit Price must be a valid number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setServiceData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSave = async () => {
    if (!validateForm()) {
        toast.error("Please fix the form errors.");
        return;
    }

    setIsLoading(true);
    try {
      const payload = {
        serviceName: serviceData.serviceName.trim(),
        particulars: serviceData.particulars.trim(),
        providedBy: serviceData.providedBy.trim(),
        unitPrice: parseFloat(serviceData.unitPrice),
      };

      if (isEditMode) {
        await axios.put(
          `https://api.muktihospital.com/api/emergency/services/edit/${serviceToEdit.id}`,
          payload,
          {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          }
        );
        toast.success("Service updated successfully!");
      } else {
        await axios.post(
          "https://api.muktihospital.com/api/emergency/services/add",
          payload,
          {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          }
        );
        toast.success("Service added successfully!");
      }
      onSave();
      onClose();
    } catch (error) {
      console.error("Error saving service:", error);
      toast.error(error.response?.data?.error || "Failed to save service.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[100vh] overflow-y-auto">
        
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700">
            {isEditMode ? "Edit Service" : "Add Service"}
          </h2>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          
          {/* Service Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="serviceName"
              value={serviceData.serviceName}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                errors.serviceName ? "border-red-500" : ""
              }`}
              placeholder="Enter service name"
              disabled={isLoading}
            />
            {errors.serviceName && (
              <span className="text-red-500 text-xs mt-1">{errors.serviceName}</span>
            )}
          </div>

          {/* Particulars */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Particulars
            </label>
            <input
              type="text"
              name="particulars"
              value={serviceData.particulars}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                errors.particulars ? "border-red-500" : ""
              }`}
              placeholder="Enter particulars"
              disabled={isLoading}
            />
            {errors.particulars && (
              <span className="text-red-500 text-xs mt-1">{errors.particulars}</span>
            )}
          </div>

          {/* Provided By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Provided By
            </label>
            <input
              type="text"
              name="providedBy"
              value={serviceData.providedBy}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                errors.providedBy ? "border-red-500" : ""
              }`}
              placeholder="e.g. Doctor / Nurse"
              disabled={isLoading}
            />
            {errors.providedBy && (
              <span className="text-red-500 text-xs mt-1">{errors.providedBy}</span>
            )}
          </div>

          {/* Unit Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unit Price <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="unitPrice"
              value={serviceData.unitPrice}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                errors.unitPrice ? "border-red-500" : ""
              }`}
              placeholder="0.00"
              disabled={isLoading}
            />
            {errors.unitPrice && (
              <span className="text-red-500 text-xs mt-1">{errors.unitPrice}</span>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default EmergencyServiceModal;